/**
 * Pre-send confirmation for the single-document testdata e-mail
 * (`XMLInsights-SDD` `REQ-TESTDATA-011`, CAP-003 v1.6).
 *
 * Invariant: what-you-review == what-you-send. The modal receives ONE frozen
 * request snapshot (taken by `openEmailModal` right after validation, from the
 * canonical `buildRequestBody`). Both the summary and the POST payload are
 * derived from that snapshot by the pure functions below — never from live
 * generator state — so they can be tested side by side.
 *
 * Kept free of React/i18n and imported with explicit `.ts` extensions so it
 * runs under Node's built-in test runner (see sendConfirmation.test.ts).
 */
import { COUNTRY_OPTIONS } from '../../config/locales.ts'

/** The request body as built by `buildRequestBody()` at modal-open time. */
export type EmailRequestSnapshot = Readonly<Record<string, unknown>>

/** Modal-owned mail choices (recipient, attachments, subject note). */
export interface SendChoices {
  toAddress: string
  includePdf: boolean
  includeXml: boolean
  embedPdfInXml: boolean
  subjectNote: string
}

/** Server-owned display context from `/api/testdata/send-confirmation-context`. */
export interface SendConfirmationContext {
  sender: string | null
  locale: string
  language: string
  date_example: string
  amount_example: string
}

export interface SendConfirmationSummary {
  sender: string | null
  recipient: string
  documentType: string
  xmlFormat: string
  /** Only set when it actually applies to the snapshot's format. */
  xmlProfile: string | null
  facturxProfile: string | null
  country: string
  countryName: string
  locale: string
  /** Language code from the canonical locale profile; null until loaded. */
  language: string | null
  currency: string
  /** Only set when a Financial Scenario is active in the snapshot. */
  financialScenarioId: string | null
  lineCount: number
  dateExample: string | null
  amountExample: string | null
}

const EMBEDDABLE_XML_FORMATS = new Set(['sales005', 'ubl21'])

const str = (v: unknown): string => (typeof v === 'string' ? v : '')

export function isFacturxSnapshot(snapshot: EmailRequestSnapshot): boolean {
  return snapshot.xml_format === 'facturx'
}

/** Whether the "embed PDF in XML" choice applies to this snapshot/choice. */
export function embedOptionApplies(snapshot: EmailRequestSnapshot, includeXml: boolean): boolean {
  return !isFacturxSnapshot(snapshot) && includeXml && EMBEDDABLE_XML_FORMATS.has(str(snapshot.xml_format))
}

/** Copy of the snapshot frozen at the top level; `lines` is copied too. */
export function freezeSnapshot(body: Record<string, unknown>): EmailRequestSnapshot {
  const lines = Array.isArray(body.lines) ? Object.freeze([...body.lines]) : body.lines
  return Object.freeze({ ...body, lines })
}

/** The exact body posted to `/api/testdata/send-email`. */
export function buildSendEmailPayload(
  snapshot: EmailRequestSnapshot,
  choices: SendChoices,
  documentNumber: string,
): Record<string, unknown> {
  return {
    ...snapshot,
    document_number: documentNumber,
    to_address: choices.toAddress,
    include_pdf: choices.includePdf,
    include_xml: choices.includeXml,
    embed_pdf_in_xml: embedOptionApplies(snapshot, choices.includeXml) ? choices.embedPdfInXml : true,
    // include_ground_truth comes from the snapshot (central generator
    // setting, REQ-TESTREF-010) — the modal never overrides it.
    subject_note: choices.subjectNote.trim() || null,
  }
}

/** What the confirmation modal shows — derived from the same snapshot. */
export function buildSendConfirmationSummary(
  snapshot: EmailRequestSnapshot,
  choices: Pick<SendChoices, 'toAddress'>,
  context: SendConfirmationContext | null,
): SendConfirmationSummary {
  const xmlFormat = str(snapshot.xml_format)
  const country = str(snapshot.country)
  const locale = str(snapshot.locale)
  // Context belongs to a specific locale; never show examples of another one.
  const ctx = context && context.locale === locale ? context : null
  return {
    sender: ctx?.sender ?? null,
    recipient: choices.toAddress,
    documentType: str(snapshot.document_type),
    xmlFormat,
    xmlProfile: str(snapshot.xml_profile) || null,
    facturxProfile: xmlFormat === 'facturx' ? str(snapshot.facturx_profile) || null : null,
    country,
    countryName: COUNTRY_OPTIONS.find(o => o.value === country)?.name ?? country,
    locale,
    language: ctx?.language ?? null,
    currency: str(snapshot.currency),
    financialScenarioId: str(snapshot.financial_scenario_id) || null,
    lineCount: Array.isArray(snapshot.lines) ? snapshot.lines.length : 0,
    dateExample: ctx?.date_example ?? null,
    amountExample: ctx?.amount_example ?? null,
  }
}

/**
 * Single-flight guard: one confirmation sends at most one mail. React state
 * (`phase`) updates asynchronously, so two clicks in the same tick would both
 * still see `phase === 'confirm'`; this synchronous gate closes that window.
 */
export function createSendGate() {
  let inFlight = false
  return {
    tryAcquire(): boolean {
      if (inFlight) return false
      inFlight = true
      return true
    },
    release(): void {
      inFlight = false
    },
    get inFlight(): boolean {
      return inFlight
    },
  }
}
